import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';
import {
  AppUserUpdate,
  AppUserRole,
  appDelay,
  SupplierPaymentTerm,
  DeliveryAddress,
} from '@dill/dill-shared';
import { firebaseFileUploadHelper } from '../../utils/helpers';

export const getUserSuppliers = createAsyncThunk(
  'supplier/getUserSuppliers',
  async (data, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        BASE_API_URL + '/invoicesAPI/distributors',
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getUserCustomers = createAsyncThunk(
  'supplier/getUserCustomers',
  async (
    {
      supplierId,
      previousLastDocumentId,
      pageSize,
      sortBy,
      sortOrder,
    }: {
      supplierId: string;
      previousLastDocumentId?: string;
      pageSize: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let params: any = { pageSize };
      if (previousLastDocumentId) {
        params = { ...params, previousLastDocumentId };
      }
      if (sortBy) {
        params = { ...params, sortBy };
      }
      if (sortOrder) {
        params = { ...params, sortOrder };
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/getCustomers`,
        {
          params,
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierSummary = createAsyncThunk(
  'supplier/getSupplierSummary',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/getSupplierSummary`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getUserInvoices = createAsyncThunk(
  'supplier/getUserInvoices',
  async (data: { weeksLimit?: number; supplierId?: string } | null, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let url = `${BASE_API_URL}/invoicesAPI/${data?.supplierId}/supplierInvoices`;
      if (data && data.weeksLimit) {
        url = `${url}?weeksLimit=${data.weeksLimit}`;
      }
      const response = await axios.get<AppServerResponse>(url, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getUserSalesReceipts = createAsyncThunk(
  'supplier/getUserSalesReceipts',
  async (data: { weeksLimit?: number } | null, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let url = `${BASE_API_URL}/salesReceiptsAPI/supplierSalesReceipts`;
      if (data && data.weeksLimit) {
        url = `${BASE_API_URL}/salesReceiptsAPI/supplierSalesReceipts?weeksLimit=${data.weeksLimit}`;
      }
      const response = await axios.get<AppServerResponse>(url, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getUserDrivers = createAsyncThunk(
  'supplier/getUserDrivers',
  async (data, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(BASE_API_URL + '/driversAPI/drivers', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const assignDriverToInvoice = createAsyncThunk(
  'supplier/assignDriverToInvoice',
  async (
    {
      invoiceId,
      driverId,
      weeksLimit,
      supplierId,
    }: { invoiceId: string; driverId: string; weeksLimit: number; supplierId?: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/invoicesAPI/${supplierId}/supplierInvoices/${invoiceId}/driver`,
        { driverId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      thunkApi.dispatch(getUserInvoices({ weeksLimit, supplierId }));
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSupplier = createAsyncThunk(
  'supplier/update',
  async (
    {
      supplierId,
      supplierDetails,
    }: {
      supplierId: string;
      // TODO: Add the type of data
      supplierDetails: any;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/updateSupplier/${supplierId}`,
        { ...supplierDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSupplierReminderSettings = createAsyncThunk(
  'supplier/updateReminderSettings',
  async (
    {
      supplierId,
      invoicePaymentReminderSettings,
    }: {
      supplierId: string;
      invoicePaymentReminderSettings: any;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/updateReminderSettings`,
        { invoicePaymentReminderSettings },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSupplierFeeWaiverSettings = createAsyncThunk(
  'supplier/updateFeeWaiverSettings',
  async (
    {
      supplierId,
      creditCardFeeWaiverConditions,
      overwriteExisting,
    }: {
      supplierId: string;
      creditCardFeeWaiverConditions: {
        waiveForCurrentDuePayments?: boolean;
        waiveForDiscountDatePayments?: boolean;
        currentDueWaiverDayOfMonthCutoff?: number | null;
      };
      overwriteExisting?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/updateFeeWaiverSettings`,
        { creditCardFeeWaiverConditions, overwriteExisting },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSupplierFiservBuyerFeeRate = createAsyncThunk(
  'supplier/updateFiservBuyerFeeRate',
  async (
    {
      supplierId,
      fiservBuyerFeeRate,
    }: {
      supplierId: string;
      fiservBuyerFeeRate: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/updateFiservBuyerFeeRate`,
        { fiservBuyerFeeRate },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSupplierDefaultBuyerDisabledPaymentTypes = createAsyncThunk(
  'supplier/updateDefaultBuyerDisabledPaymentTypes',
  async (
    {
      supplierId,
      defaultBuyerDisabledPaymentTypes,
      overwriteExisting,
    }: {
      supplierId: string;
      defaultBuyerDisabledPaymentTypes: ('CREDIT_CARD' | 'DEBIT_CARD' | 'BANK')[];
      overwriteExisting: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/updateDefaultBuyerDisabledPaymentTypes`,
        { defaultBuyerDisabledPaymentTypes, overwriteExisting },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const sendEmailStatementsForBuyers = createAsyncThunk(
  'supplier/sendEmailStatementsForBuyers',
  async (
    {
      supplierId,
      buyerIds,
      jobIds,
      jobNames,
      jobsSummary,
      additionalEmails,
      excludedUserIds,
      subjectLine,
      notes,
      attachments,
      isEmailEnabled,
      mailingAddresses,
      faxNumbers,
    }: {
      supplierId: string;
      buyerIds: string[];
      jobIds?: string[];
      jobNames?: string[];
      jobsSummary?: {
        totalAmountDue: number;
        totalCurrentDue: number;
        totalPastDue1To30: number;
        totalPastDue31To60: number;
        totalPastDue61To90: number;
        totalPastDueOver90: number;
      };
      additionalEmails?: string[];
      excludedUserIds?: string[];
      subjectLine?: string;
      notes?: string;
      attachments?: File[];
      // One-off channels from Send Statement (mail / fax only for a single current statement).
      isEmailEnabled?: boolean;
      mailingAddresses?: DeliveryAddress[];
      faxNumbers?: string[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      // Upload attachments to Firebase if provided
      const uploadedAttachments: { url: string; name: string }[] = [];
      if (attachments && attachments.length > 0) {
        for (const attachment of attachments) {
          const blobUrl = URL.createObjectURL(attachment);
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-${attachment.name}`,
            storagePath: `statement-emails/${supplierId}/${buyerIds[0] ?? 'bulk'}`,
          });
          if (downloadUrl) {
            uploadedAttachments.push({ url: downloadUrl, name: attachment.name });
          }
        }
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/quickBooksAuth/buyersEmailStatement`,
        {
          buyerIds,
          ...(jobIds && jobIds.length > 0 ? { jobIds } : {}),
          ...(jobNames && jobNames.length > 0 ? { jobNames } : {}),
          ...(jobsSummary ? { jobsSummary } : {}),
          ...(additionalEmails && additionalEmails.length > 0 ? { additionalEmails } : {}),
          ...(excludedUserIds && excludedUserIds.length > 0 ? { excludedUserIds } : {}),
          ...(subjectLine ? { subjectLine } : {}),
          ...(notes ? { notes } : {}),
          ...(isEmailEnabled === false ? { isEmailEnabled } : {}),
          ...(mailingAddresses?.length ? { mailingAddresses } : {}),
          ...(faxNumbers?.length ? { faxNumbers } : {}),
          ...(uploadedAttachments.length > 0 ? { attachments: uploadedAttachments } : {}),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const addDistributionOneSupplierConnection = createAsyncThunk(
  'supplier/addDistributionOneSupplierConnection',
  async (
    {
      supplierId,
      baseUrl,
      companyName,
      userName,
      password,
    }: {
      supplierId: string;
      baseUrl: string;
      companyName: string;
      userName: string;
      password: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/accountingIntegrations/addDistributionOneSupplierConnection`,
        { baseUrl, companyName, userName, password },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const addSage100SupplierConnection = createAsyncThunk(
  'supplier/addSage100SupplierConnection',
  async (
    {
      supplierId,
      clientId,
      clientSecrete,
      apiScope,
    }: {
      supplierId: string;
      clientId: string;
      clientSecrete: string;
      apiScope: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/accountingIntegrations/addSage100SupplierConnection`,
        { clientId, clientSecrete, apiScope },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const addP21SupplierConnection = createAsyncThunk(
  'supplier/addP21SupplierConnection',
  async (
    {
      supplierId,
      companyId,
      companyBaseUrl,
      companyAPIBaseUrl,
      bankCoNo,
      userName,
      password,
      clientSecret,
      useBillToShipToSplit,
    }: {
      supplierId: string;
      companyId: string;
      companyBaseUrl: string;
      companyAPIBaseUrl: string;
      bankCoNo: string;
      userName: string;
      password?: string;
      clientSecret?: string;
      useBillToShipToSplit?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/accountingIntegrations/addP21SupplierConnection`,
        {
          companyBaseUrl,
          companyAPIBaseUrl,
          bankCoNo,
          companyId,
          userName,
          password,
          clientSecret,
          useBillToShipToSplit,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const addEclipseSupplierConnection = createAsyncThunk(
  'supplier/addEclipseSupplierConnection',
  async (
    {
      supplierId,
      baseUrl,
      userName,
      password,
    }: {
      supplierId: string;
      baseUrl: string;
      userName: string;
      password: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/accountingIntegrations/addEclipseSupplierConnection`,
        { baseUrl, userName, password },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const addDmsiSupplierConnection = createAsyncThunk(
  'supplier/addDmsiSupplierConnection',
  async (
    {
      supplierId,
      baseUrl,
      loginId,
      password,
    }: {
      supplierId: string;
      baseUrl: string;
      loginId: string;
      password: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/accountingIntegrations/addDmsiSupplierConnection`,
        { baseUrl, loginId, password },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const sendEmailStatementsForAllBuyers = createAsyncThunk(
  'supplier/sendEmailStatementsForAllBuyers',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/quickBooksAuth/allBuyersEmailStatement`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const sendAllBuyersStatementFromSnapshot = createAsyncThunk(
  'supplier/sendAllBuyersStatementFromSnapshot',
  async ({ statementId, supplierId }: { statementId: string; supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/quickBooksAuth/allBuyersStatementFromSnapshot`,
        { statementId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const clearStatementSendResults = createAsyncThunk(
  'supplier/clearStatementSendResults',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) return null;
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/quickBooksAuth/clearStatementSendResults`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error?.message);
    }
  }
);

export const saveMonthlyStatementSnapshots = createAsyncThunk(
  'supplier/saveMonthlyStatementSnapshots',
  async ({ month, supplierId }: { month: string; supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/quickBooksAuth/saveMonthlyStatementSnapshots`,
        { month },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getUserCustomer = createAsyncThunk(
  'supplier/getUserCustomer',
  async (
    {
      supplierId,
      buyerId,
    }: {
      supplierId: string;
      buyerId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/getCustomer/${buyerId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getMainBuyerForPreview = createAsyncThunk(
  'supplier/getMainBuyerForPreview',
  async (
    {
      supplierId,
      buyerId,
    }: {
      supplierId: string;
      buyerId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/getMainBuyerForPreview/${buyerId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const recursiveGetUserCustomers = createAsyncThunk(
  'supplier/recursiveGetUserCustomers',
  async (
    {
      supplierId,
      previousLastDocumentId,
      pageSize,
      sortBy,
      sortOrder,
    }: {
      supplierId: string;
      previousLastDocumentId?: string;
      pageSize: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let params: any = { pageSize };
      if (previousLastDocumentId) {
        params = { ...params, previousLastDocumentId };
      }
      if (sortBy) {
        params = { ...params, sortBy };
      }
      if (sortOrder) {
        params = { ...params, sortOrder };
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/getCustomers`,
        {
          params,
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const validateFiservMerchant = createAsyncThunk(
  'supplier/validateFiservMerchant',
  async (
    {
      supplierId,
      merchantId,
    }: {
      supplierId: string;
      merchantId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/fiserv/validateFiservMerchant/${merchantId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const addSupplierUser = createAsyncThunk(
  'supplier/addSupplierUser',
  async (
    {
      supplierId,
      email,
      firstName,
      lastName,
    }: {
      supplierId: string;
      email: string;
      firstName: string;
      lastName: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/addSupplierUser`,
        { email, firstName, lastName },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const addUser = createAsyncThunk(
  'supplier/addUser',
  async (
    {
      supplierId,
      email,
      firstName,
      lastName,
      roles,
      overrideRoles = false,
    }: {
      supplierId: string;
      email: string;
      firstName: string;
      lastName: string;
      roles?: AppUserRole[];
      overrideRoles?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/addUser`,
        { email, firstName, lastName, roles, overrideRoles },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const updateUser = createAsyncThunk(
  'supplier/updateUser',
  async (
    {
      supplierId,
      userDetails,
      userId,
    }: {
      supplierId: string;
      userDetails: AppUserUpdate;
      userId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/updateUser/${userId}`,
        { ...userDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'supplier/deleteUser',
  async (
    {
      supplierId,
      userId,
    }: {
      supplierId: string;
      userId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/deleteUser/${userId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const removeSupplierUser = createAsyncThunk(
  'supplier/removeSupplierUser',
  async (
    {
      supplierId,
      userId,
    }: {
      supplierId: string;
      userId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/removeUser/${userId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { ...response.data, removedUserId: userId };
    } catch (error: any) {
      console.log(error);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const resendSupplierUserInvite = createAsyncThunk(
  'supplier/resendSupplierUserInvite',
  async (
    {
      supplierId,
      userId,
    }: {
      supplierId: string;
      userId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/resendUserInvite/${userId}`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

// The payment terms this supplier can approve a buyer on. The backend decides the source — an ERP's
// own terms table where one is synced, otherwise the terms configured in Dill — so callers stay
// integration-agnostic. No slice keeps them; callers read the thunk's payload directly.
export const getSupplierPaymentTerms = createAsyncThunk(
  'supplier/getSupplierPaymentTerms',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<{ success: boolean; data: SupplierPaymentTerm[] }>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentTerms`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
